import { format } from 'date-fns';
import { useState } from 'react';
import type { Event } from '@/types/calendar';
import { validateTimeRange, getMinEndTime } from '@/utils/timeValidation';

interface EventDetailsModalProps {
  event: Event;
  onClose: () => void;
  onEdit: (event: Event) => void;
  onDelete: (eventId: string) => Promise<void>;
}

export default function EventDetailsModal({ event, onClose, onEdit, onDelete }: EventDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description || '');
  const [startTime, setStartTime] = useState(event.start_time);
  const [endTime, setEndTime] = useState(event.end_time);
  const [type, setType] = useState(event.type);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = () => {
    if (!validateTimeRange(startTime, endTime)) {
      return; // Prevent saving if times are invalid
    }

    onEdit({
      ...event,
      title,
      description,
      start_time: startTime,
      end_time: endTime,
      type
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(event.id);
      onClose();
    } catch (error) {
      console.error('Error deleting event:', error);
      setIsDeleting(false);
    }
  };

  const handleTimeChange = (newStartTime: string) => {
    setStartTime(newStartTime);
    if (!validateTimeRange(newStartTime, endTime)) {
      setEndTime(getMinEndTime(newStartTime));
    }
  };

  const handleEndTimeChange = (newEndTime: string) => {
    if (validateTimeRange(startTime, newEndTime)) {
      setEndTime(newEndTime);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-light rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            {isEditing ? 'Edit Event' : 'Event Details'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg p-2 rounded-full transition-colors"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                  Event Type
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as Event['type'])}
                  className="select w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                >
                  <option value="reservation">Reservation</option>
                  <option value="event">Event</option>
                  <option value="task">Task</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                />
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    Start Time
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => handleTimeChange(e.target.value)}
                      className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                      style={{ colorScheme: 'light dark' }}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="fas fa-clock text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    End Time
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => handleEndTimeChange(e.target.value)}
                      className="input w-full bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 px-4 h-11"
                      style={{ colorScheme: 'light dark' }}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="fas fa-clock text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="textarea w-full h-24 bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text-primary border-2 border-gray-200 dark:border-dark-border rounded-lg focus:border-primary-500 dark:focus:border-primary-500 p-4 resize-none"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEventTypeColor(type)}`}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
                <span className="text-gray-500 dark:text-dark-text-secondary text-sm">
                  {startTime.substring(0, 5)} - {endTime.substring(0, 5)}
                </span>
              </div>
              
              <h4 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
                {title}
              </h4>
              
              {description && (
                <p className="text-gray-600 dark:text-dark-text-secondary whitespace-pre-wrap text-sm">
                  {description}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex justify-end space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="btn btn-outline btn-outline-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-outline btn-outline-secondary"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getEventTypeColor(type: Event['type']) {
  switch (type) {
    case 'reservation':
      return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400';
    case 'event':
      return 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400';
    case 'task':
      return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400';
  }
} 